import { GridApiPro, GridColDef } from "@mui/x-data-grid-pro";
import { useSnackbar } from "notistack";
import { MutableRefObject, useState } from "react";
import { useTranslation } from "react-i18next";
import { AuditRow } from "../models/AuditRow";
import { AuditTrip } from "../models/AuditTrip";
import { AuditView } from "../models/AuditView";
import { buildAuditReport, buildSummaryReport, SummaryLabels } from "../utils/auditReport";
import { PeriodSummary } from "../utils/periodSummary";
import { AuditFilters } from "./useAuditFilters";
import { DriverLabel } from "./useDriverLabels";
import { useExcelExport } from "@/hooks/export/useExcelExport";
import { Report } from "@/utilities/export/report";

interface Args {
  apiRef: MutableRefObject<GridApiPro>;
  columns: GridColDef<AuditRow>[];
  rows: AuditRow[];
  view: AuditView;
  filters: AuditFilters;
  timeZone: string;
  distanceDecimals: number;
  driverLabel: DriverLabel;
  getStartAddress: (trip: AuditTrip) => string;
  getStopAddress: (trip: AuditTrip) => string;
  summary: PeriodSummary;
  /** Whether the period allows the consumption split (at least one full calendar month). */
  includeSplit: boolean;
  /** Vehicles loaded and in the filter; when not all are loaded the header says so. */
  vehicles: { shown: number; total: number };
}

/**
 * The two Excel exports of the audit page (full report, period summary only) on the shared
 * export pipeline. `exporting` is true while one runs, so the page can lock both buttons.
 */
export function useAuditExport({
  apiRef,
  columns,
  rows,
  view,
  filters,
  timeZone,
  distanceDecimals,
  driverLabel,
  getStartAddress,
  getStopAddress,
  summary,
  includeSplit,
  vehicles,
}: Args) {
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { exportExcel } = useExcelExport();
  const [exporting, setExporting] = useState(false);

  const header = (title: string) => ({
    title,
    fromDay: filters.fromDay,
    toDay: filters.toDay,
    filter: [
      ...filters.groups.map(group => group.name),
      ...filters.devices.map(device => device.name),
      ...filters.reasons,
    ]
      .filter((name): name is string => !!name)
      .join(", "),
    extra: [
      [t("logbookAudit.export.view"), t(`logbookAudit.viewSwitch.${view}`)],
      [t("logbookAudit.export.timeZone"), timeZone],
      ...(vehicles.shown < vehicles.total
        ? [[t("logbookAudit.export.vehicles"), t("logbookAudit.export.vehiclesPartial", { shown: vehicles.shown, total: vehicles.total })]]
        : []),
    ] as [string, string][],
  });

  const summaryLabels = (): SummaryLabels => ({
    sheet: t("logbookAudit.summary.title"),
    vehicle: t("logbookAudit.summary.columns.vehicle"),
    kmTotal: t("logbookAudit.summary.columns.kmTotal"),
    kmFavored: t("logbookAudit.summary.columns.kmFavored"),
    kmNotFavored: t("logbookAudit.summary.columns.kmNotFavored"),
    consumption: t("logbookAudit.summary.columns.consumption"),
    refuel: t("logbookAudit.summary.columns.refuel"),
    consumptionFavored: t("logbookAudit.summary.columns.consumptionFavored"),
    consumptionNotFavored: t("logbookAudit.summary.columns.consumptionNotFavored"),
    consumptionNone: t("logbookAudit.summary.columns.consumptionNone"),
    splitBasis: t("logbookAudit.summary.columns.splitBasis"),
    splitBasisValues: {
      consumption: t("logbookAudit.summary.splitBasis.consumption"),
      refuel: t("logbookAudit.summary.splitBasis.refuel"),
    },
    unit: t("logbookAudit.summary.columns.unit"),
    total: t("logbookAudit.summary.total"),
  });

  const run = async (build: () => Report) => {
    setExporting(true);
    try {
      await exportExcel(build());
    } catch (error) {
      console.error(error);
      enqueueSnackbar(t("logbookAudit.export.failed"), { variant: "error" });
    } finally {
      setExporting(false);
    }
  };

  const exportAudit = () =>
    run(() =>
      buildAuditReport({
        ...header(t("logbookAudit.title")),
        apiRef,
        columns,
        rows,
        view,
        timeZone,
        language: i18n.language,
        distanceDecimals,
        getStartAddress,
        getStopAddress,
        summary,
        summaryLabels: summaryLabels(),
        includeSplit,
        refuelUnitHint: t("logbookAudit.export.refuelUnitHint"),
        levelColumn: {
          header: t("logbookAudit.export.level"),
          day: t("logbookAudit.export.levelDay"),
          trip: t("logbookAudit.export.levelTrip"),
        },
        deviceHeader: t("logbookAudit.columns.device"),
        driverLabel,
        fuelTypeLabels: {
          electric: t("logbookAudit.fuelType.electric"),
          diesel: t("logbookAudit.fuelType.diesel"),
          cng: t("logbookAudit.fuelType.cng"),
          petrol: t("logbookAudit.fuelType.petrol"),
        },
      }),
    );

  const exportSummary = () =>
    run(() =>
      buildSummaryReport({
        ...header(`${t("logbookAudit.title")} – ${t("logbookAudit.summary.title")}`),
        summary,
        summaryLabels: summaryLabels(),
        includeSplit,
      }),
    );

  return { exportAudit, exportSummary, exporting };
}
